/**
 * TilePyramidManager - Multi-zoom tile management
 *
 * Handles tile loading, caching, drawing, and saving with a tile pyramid structure.
 * Drawing always occurs at DETAIL_ZOOM level (0 = highest detail) and automatically
 * propagates to higher zoom levels (lower detail).
 */

import {
  TILE_SIZE,
  MAX_ZOOM,
  DETAIL_ZOOM,
  getTileWorldSize,
  worldToTile,
  tileToWorld,
  getTileKey,
  getOptimalZoom,
  getVisibleTiles,
  sortTilesByDistance,
  TileCoord,
  CachedTile
} from '../types/tileSystem';
import { tilesApi } from './api';

/**
 * Cached stamp for brush rendering
 */
interface CachedStamp {
  canvas: HTMLCanvasElement;
  lastAccess: number;
}

/**
 * Represents a single brush stroke for undo/redo and deferred save
 */
interface BrushStroke {
  id: number;
  worldX: number;
  worldY: number;
  materialName: string | null; // null for eraser
  brushName: string;
  size: number;
  rotation: number;
  strength: number;
  isEraser: boolean;
  /** Affected tiles at DETAIL_ZOOM (highest detail level) */
  affectedTiles: Array<{ x: number; y: number }>;
}

/**
 * Background configuration for eraser rendering
 */
interface BackgroundConfig {
  materialName: string | null;
  /** Custom background color (hex format, e.g., "#1a1a1a") */
  backgroundColor: string | null;
  randomRotation: boolean;
}

/**
 * Configuration constants
 */
const MAX_CACHED_TILES = 1024;
const MAX_CACHED_STAMPS = 64;
const MIN_TILE_AGE_MS = 30000;
const MAX_CONCURRENT_LOADS = 8;

/** Default background color when no material is set (matches MapCanvas) */
const DEFAULT_BACKGROUND_COLOR = '#1a1a1a';

/**
 * TilePyramidManager handles multi-zoom tile operations
 */
export class TilePyramidManager {
  private mapId: string;
  private tiles: Map<string, CachedTile> = new Map();
  private dirtyTiles: Set<string> = new Set();
  private loadingTiles: Set<string> = new Set();
  private loadQueue: TileCoord[] = [];
  private activeLoads = 0;

  private materialImages: Map<string, HTMLImageElement>;
  private brushImages: Map<string, HTMLImageElement>;

  private stampCache: Map<string, CachedStamp> = new Map();
  private eraserStampCache: Map<string, CachedStamp> = new Map();

  // Background configuration for eraser stamps
  private backgroundConfig: BackgroundConfig = {
    materialName: null,
    backgroundColor: null,
    randomRotation: false
  };

  private renderingTiles: Set<string> = new Set();

  // Stroke history for undo/redo and deferred save
  private strokes: BrushStroke[] = [];
  private redoStack: BrushStroke[] = [];
  private nextStrokeId = 1;

  // Overlay canvas per tile (for preview rendering)
  private overlayTiles: Map<string, HTMLCanvasElement> = new Map();
  private dirtyOverlayTiles: Set<string> = new Set();

  // Last viewport params for reloading after save
  private lastViewport: {
    x: number;
    y: number;
    width: number;
    height: number;
    zoom: number;
  } | null = null;

  // Cache for visible tiles to avoid recalculating every frame
  private visibleTilesCache: {
    tiles: TileCoord[];
    viewportX: number;
    viewportY: number;
    viewportZoom: number;
    canvasWidth: number;
    canvasHeight: number;
  } | null = null;

  // Cache-busting version, incremented after each save
  private cacheVersion = 0;

  // Tiles that need cache-busting (recently saved)
  private tilesToBustCache: Set<string> = new Set();

  constructor(
    mapId: string,
    materialImages: Map<string, HTMLImageElement>,
    brushImages: Map<string, HTMLImageElement>
  ) {
    this.mapId = mapId;
    this.materialImages = materialImages;
    this.brushImages = brushImages;
  }

  /**
   * Updates asset images (materials and brushes)
   */
  updateImages(
    materialImages: Map<string, HTMLImageElement>,
    brushImages: Map<string, HTMLImageElement>
  ): void {
    this.materialImages = materialImages;
    this.brushImages = brushImages;
    this.stampCache.clear();
    this.eraserStampCache.clear();
  }

  /**
   * Updates background configuration for eraser rendering
   * Clears eraser stamp cache when config changes
   */
  updateBackgroundConfig(materialName: string | null, backgroundColor: string | null, randomRotation: boolean): void {
    if (this.backgroundConfig.materialName !== materialName ||
        this.backgroundConfig.backgroundColor !== backgroundColor ||
        this.backgroundConfig.randomRotation !== randomRotation) {
      this.backgroundConfig = { materialName, backgroundColor, randomRotation };
      // Clear eraser stamp cache since background changed
      this.eraserStampCache.clear();
    }
  }

  // ===========================================
  // STAMP CACHE MANAGEMENT
  // ===========================================

  /**
   * Gets or creates a brush stamp with material
   */
  private getOrCreateStamp(
    materialName: string,
    brushName: string,
    size: number,
    strength: number
  ): HTMLCanvasElement | null {
    const materialImg = this.materialImages.get(materialName);
    const brushImg = this.brushImages.get(brushName);
    if (!materialImg || !brushImg) return null;

    const stampSize = Math.max(1, Math.round(size));
    const strengthKey = Math.round(strength * 100);
    const cacheKey = `${materialName}|${brushName}|${stampSize}|${strengthKey}`;

    const cached = this.stampCache.get(cacheKey);
    if (cached) {
      cached.lastAccess = Date.now();
      return cached.canvas;
    }

    const stampCanvas = document.createElement('canvas');
    stampCanvas.width = stampSize;
    stampCanvas.height = stampSize;
    const stampCtx = stampCanvas.getContext('2d');
    if (!stampCtx) return null;

    // Create alpha mask from brush
    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = stampSize;
    maskCanvas.height = stampSize;
    const maskCtx = maskCanvas.getContext('2d');
    if (!maskCtx) return null;

    maskCtx.drawImage(brushImg, 0, 0, stampSize, stampSize);
    const imageData = maskCtx.getImageData(0, 0, stampSize, stampSize);
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
      const luminosity = data[i];
      data[i] = 255;
      data[i + 1] = 255;
      data[i + 2] = 255;
      data[i + 3] = Math.round(luminosity * strength);
    }
    maskCtx.putImageData(imageData, 0, 0);

    // Tile material onto stamp
    const matSize = Math.max(1, Math.min(materialImg.width, materialImg.height, stampSize));
    const tilesX = Math.ceil(stampSize / matSize);
    const tilesY = Math.ceil(stampSize / matSize);

    for (let tx = 0; tx < tilesX; tx++) {
      for (let ty = 0; ty < tilesY; ty++) {
        stampCtx.drawImage(
          materialImg,
          0, 0, materialImg.width, materialImg.height,
          tx * matSize, ty * matSize, matSize, matSize
        );
      }
    }

    // Apply mask
    stampCtx.globalCompositeOperation = 'destination-in';
    stampCtx.drawImage(maskCanvas, 0, 0);

    this.stampCache.set(cacheKey, {
      canvas: stampCanvas,
      lastAccess: Date.now()
    });

    this.pruneStampCache();
    return stampCanvas;
  }

  /**
   * Gets or creates an eraser stamp that draws the background texture/color
   * This is for PREVIEW only - shows what the erased area will look like
   */
  private getOrCreateEraserStamp(
    brushName: string,
    size: number,
    strength: number
  ): HTMLCanvasElement | null {
    const brushImg = this.brushImages.get(brushName);
    if (!brushImg) return null;

    const stampSize = Math.max(1, Math.round(size));
    const strengthKey = Math.round(strength * 100);
    const bgMaterial = this.backgroundConfig.materialName || 'default';
    const bgColor = this.backgroundConfig.backgroundColor || 'default';
    const cacheKey = `preview|${brushName}|${stampSize}|${strengthKey}|${bgMaterial}|${bgColor}`;

    const cached = this.eraserStampCache.get(cacheKey);
    if (cached) {
      cached.lastAccess = Date.now();
      return cached.canvas;
    }

    const stampCanvas = document.createElement('canvas');
    stampCanvas.width = stampSize;
    stampCanvas.height = stampSize;
    const stampCtx = stampCanvas.getContext('2d');
    if (!stampCtx) return null;

    // Create alpha mask from brush
    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = stampSize;
    maskCanvas.height = stampSize;
    const maskCtx = maskCanvas.getContext('2d');
    if (!maskCtx) return null;

    maskCtx.drawImage(brushImg, 0, 0, stampSize, stampSize);
    const imageData = maskCtx.getImageData(0, 0, stampSize, stampSize);
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
      const luminosity = data[i];
      data[i] = 255;
      data[i + 1] = 255;
      data[i + 2] = 255;
      data[i + 3] = Math.round(luminosity * strength);
    }
    maskCtx.putImageData(imageData, 0, 0);

    // Draw background onto stamp
    const bgMaterialImg = this.backgroundConfig.materialName
      ? this.materialImages.get(this.backgroundConfig.materialName)
      : null;

    if (bgMaterialImg) {
      // Tile material texture onto stamp
      const matSize = Math.max(1, Math.min(bgMaterialImg.width, bgMaterialImg.height, stampSize));
      const tilesX = Math.ceil(stampSize / matSize);
      const tilesY = Math.ceil(stampSize / matSize);

      for (let tx = 0; tx < tilesX; tx++) {
        for (let ty = 0; ty < tilesY; ty++) {
          stampCtx.drawImage(
            bgMaterialImg,
            0, 0, bgMaterialImg.width, bgMaterialImg.height,
            tx * matSize, ty * matSize, matSize, matSize
          );
        }
      }
    } else {
      // No material - use custom background color or default gray
      stampCtx.fillStyle = this.backgroundConfig.backgroundColor || DEFAULT_BACKGROUND_COLOR;
      stampCtx.fillRect(0, 0, stampSize, stampSize);
    }

    // Apply brush mask
    stampCtx.globalCompositeOperation = 'destination-in';
    stampCtx.drawImage(maskCanvas, 0, 0);

    this.eraserStampCache.set(cacheKey, {
      canvas: stampCanvas,
      lastAccess: Date.now()
    });

    this.pruneStampCache();
    return stampCanvas;
  }

  /**
   * Gets or creates a real eraser stamp for SAVING
   * This is a black mask used with destination-out to actually erase pixels
   */
  private getOrCreateRealEraserStamp(
    brushName: string,
    size: number,
    strength: number
  ): HTMLCanvasElement | null {
    const brushImg = this.brushImages.get(brushName);
    if (!brushImg) return null;

    const stampSize = Math.max(1, Math.round(size));
    const strengthKey = Math.round(strength * 100);
    const cacheKey = `real|${brushName}|${stampSize}|${strengthKey}`;

    const cached = this.eraserStampCache.get(cacheKey);
    if (cached) {
      cached.lastAccess = Date.now();
      return cached.canvas;
    }

    const stampCanvas = document.createElement('canvas');
    stampCanvas.width = stampSize;
    stampCanvas.height = stampSize;
    const stampCtx = stampCanvas.getContext('2d');
    if (!stampCtx) return null;

    stampCtx.drawImage(brushImg, 0, 0, stampSize, stampSize);
    const imageData = stampCtx.getImageData(0, 0, stampSize, stampSize);
    const data = imageData.data;

    // Create black mask with alpha from brush luminosity
    for (let i = 0; i < data.length; i += 4) {
      const luminosity = data[i];
      data[i] = 0;
      data[i + 1] = 0;
      data[i + 2] = 0;
      data[i + 3] = Math.round(luminosity * strength);
    }
    stampCtx.putImageData(imageData, 0, 0);

    this.eraserStampCache.set(cacheKey, {
      canvas: stampCanvas,
      lastAccess: Date.now()
    });

    this.pruneStampCache();
    return stampCanvas;
  }

  /**
   * Prunes stamp caches
   */
  private pruneStampCache(): void {
    if (this.stampCache.size > MAX_CACHED_STAMPS) {
      const entries = Array.from(this.stampCache.entries());
      entries.sort((a, b) => a[1].lastAccess - b[1].lastAccess);
      const toRemove = entries.slice(0, entries.length - MAX_CACHED_STAMPS + 16);
      for (const [key] of toRemove) {
        this.stampCache.delete(key);
      }
    }
    if (this.eraserStampCache.size > MAX_CACHED_STAMPS) {
      const entries = Array.from(this.eraserStampCache.entries());
      entries.sort((a, b) => a[1].lastAccess - b[1].lastAccess);
      const toRemove = entries.slice(0, entries.length - MAX_CACHED_STAMPS + 16);
      for (const [key] of toRemove) {
        this.eraserStampCache.delete(key);
      }
    }
  }

  // ===========================================
  // TILE LOADING
  // ===========================================

  // Track last load request to avoid duplicate work
  private lastLoadRequest: {
    optimalZoom: number;
    tileKeys: Set<string>;
  } | null = null;

  /**
   * Ensures tiles are loaded for the current viewport
   * Optimized to reduce work at extreme zoom levels
   */
  async ensureTilesLoaded(
    viewportX: number,
    viewportY: number,
    canvasWidth: number,
    canvasHeight: number,
    viewportZoom: number
  ): Promise<void> {
    // Store viewport params for potential reload after save
    this.lastViewport = {
      x: viewportX,
      y: viewportY,
      width: canvasWidth,
      height: canvasHeight,
      zoom: viewportZoom
    };

    const optimalZoom = getOptimalZoom(viewportZoom);
    const visibleTiles = getVisibleTiles(
      viewportX, viewportY, canvasWidth, canvasHeight, viewportZoom, optimalZoom
    );

    // Build set of tile keys we need
    const neededKeys = new Set<string>();
    for (const tile of visibleTiles) {
      neededKeys.add(getTileKey(tile.z, tile.x, tile.y));
    }

    // Skip if we already requested the same tiles
    if (this.lastLoadRequest &&
        this.lastLoadRequest.optimalZoom === optimalZoom &&
        this.lastLoadRequest.tileKeys.size === neededKeys.size) {
      let same = true;
      for (const key of neededKeys) {
        if (!this.lastLoadRequest.tileKeys.has(key)) {
          same = false;
          break;
        }
      }
      if (same) return;
    }

    this.lastLoadRequest = { optimalZoom, tileKeys: neededKeys };

    // Only queue tiles we don't have and aren't loading
    const tilesToLoad: TileCoord[] = [];
    for (const tile of visibleTiles) {
      const key = getTileKey(tile.z, tile.x, tile.y);
      if (!this.tiles.has(key) && !this.loadingTiles.has(key)) {
        tilesToLoad.push(tile);
      }
    }

    // Sort by distance only if reasonable number
    const tilesToQueue = tilesToLoad.length <= 50
      ? sortTilesByDistance(tilesToLoad, viewportX, viewportY)
      : tilesToLoad;

    // Add to queue (don't clear - keep existing queued tiles)
    for (const tile of tilesToQueue) {
      const key = getTileKey(tile.z, tile.x, tile.y);
      if (!this.loadingTiles.has(key)) {
        this.loadQueue.push(tile);
      }
    }

    // Start loading
    this.processLoadQueue();

    // Unload distant tiles (throttle at extreme zoom to reduce CPU)
    if (viewportZoom > 0.01 || Math.random() < 0.1) {
      this.unloadDistantTiles(viewportX, viewportY, viewportZoom);
    }
  }

  /**
   * Processes the tile loading queue
   */
  private processLoadQueue(): void {
    while (this.activeLoads < MAX_CONCURRENT_LOADS && this.loadQueue.length > 0) {
      const tile = this.loadQueue.shift();
      if (!tile) break;

      const key = getTileKey(tile.z, tile.x, tile.y);

      if (this.tiles.has(key) || this.loadingTiles.has(key)) {
        continue;
      }

      this.loadingTiles.add(key);
      this.activeLoads++;

      this.loadTile(tile.z, tile.x, tile.y)
        .finally(() => {
          this.loadingTiles.delete(key);
          this.activeLoads--;
          setTimeout(() => this.processLoadQueue(), 0);
        });
    }
  }

  /**
   * Loads a single tile from the server
   */
  private async loadTile(z: number, x: number, y: number): Promise<void> {
    const key = getTileKey(z, x, y);
    const baseUrl = tilesApi.getTileUrl(this.mapId, z, x, y);

    // Add cache-busting if this tile was recently saved
    const needsCacheBust = this.tilesToBustCache.has(key);
    const url = needsCacheBust ? `${baseUrl}?_v=${this.cacheVersion}` : baseUrl;

    // Remove from cache-bust set after loading
    if (needsCacheBust) {
      this.tilesToBustCache.delete(key);
    }

    return new Promise((resolve) => {
      const img = new Image();

      img.onload = () => {
        createImageBitmap(img).then(bitmap => {
          // Check if tile was already loaded/updated while we were fetching
          // This can happen if a save operation updated the cache during our fetch
          // In that case, don't overwrite - the cache has newer data with pending strokes
          if (this.tiles.has(key)) {
            bitmap.close();
            resolve();
            return;
          }

          const canvas = document.createElement('canvas');
          canvas.width = TILE_SIZE;
          canvas.height = TILE_SIZE;
          const ctx = canvas.getContext('2d');

          if (ctx) {
            ctx.drawImage(bitmap, 0, 0);
          }
          bitmap.close();

          // Apply any pending strokes to this freshly loaded tile
          // This ensures strokes drawn during loading are visible
          if (ctx && this.strokes.length > 0) {
            for (const stroke of this.strokes) {
              const affectsThisTile = stroke.affectedTiles.some(
                t => t.x === x && t.y === y && z === DETAIL_ZOOM
              );
              if (affectsThisTile) {
                this.applyStrokeToCanvas(ctx, stroke, x, y);
              }
            }
          }

          const now = Date.now();
          this.tiles.set(key, {
            canvas,
            dirty: false,
            lastAccess: now,
            hasContent: true,
            z, x, y,
            opacity: 0,
            appearTime: now
          });

          resolve();
        }).catch(() => resolve());
      };

      img.onerror = () => {
        // Check if tile was already loaded/updated while we were fetching
        if (this.tiles.has(key)) {
          resolve();
          return;
        }

        // Tile doesn't exist - mark as empty
        const canvas = document.createElement('canvas');
        canvas.width = TILE_SIZE;
        canvas.height = TILE_SIZE;

        // Apply any pending strokes to this empty tile
        const ctx = canvas.getContext('2d');
        if (ctx && this.strokes.length > 0 && z === DETAIL_ZOOM) {
          for (const stroke of this.strokes) {
            const affectsThisTile = stroke.affectedTiles.some(
              t => t.x === x && t.y === y
            );
            if (affectsThisTile) {
              this.applyStrokeToCanvas(ctx, stroke, x, y);
            }
          }
        }

        const now = Date.now();
        this.tiles.set(key, {
          canvas,
          dirty: false,
          lastAccess: now,
          hasContent: false,
          z, x, y,
          opacity: 1,
          appearTime: now
        });

        resolve();
      };

      img.src = url;
    });
  }

  /**
   * Unloads tiles that are far from the viewport
   */
  private unloadDistantTiles(viewportX: number, viewportY: number, viewportZoom: number): void {
    const now = Date.now();
    const optimalZoom = getOptimalZoom(viewportZoom);

    for (const [key, tile] of this.tiles) {
      // Never evict dirty tiles
      if (tile.dirty) continue;

      // Never evict recently accessed tiles
      if (now - tile.lastAccess < MIN_TILE_AGE_MS) continue;

      // Never evict tiles being rendered
      if (this.renderingTiles.has(key)) continue;

      // Check if tile is far from current view
      const tileWorld = tileToWorld(tile.x, tile.y, tile.z);
      const tileSize = getTileWorldSize(tile.z);
      const tileCenterX = tileWorld.x + tileSize / 2;
      const tileCenterY = tileWorld.y + tileSize / 2;

      const distance = Math.sqrt(
        Math.pow(tileCenterX - viewportX, 2) + Math.pow(tileCenterY - viewportY, 2)
      );

      // Distance threshold based on viewport size
      // Clamp to reasonable limits to avoid issues at extreme zoom levels
      const viewportSize = Math.max(1000, Math.min(10000000, 10000 / viewportZoom));

      // Also check zoom level difference
      const zoomDiff = Math.abs(tile.z - optimalZoom);

      if (distance > viewportSize * 3 || zoomDiff > 3) {
        this.tiles.delete(key);
      }
    }

    // Additional pruning if over limit
    if (this.tiles.size > MAX_CACHED_TILES) {
      this.pruneOldTiles();
    }
  }

  /**
   * Prunes oldest non-dirty tiles from cache
   */
  private pruneOldTiles(): void {
    const now = Date.now();
    const candidates: Array<{ key: string; lastAccess: number }> = [];

    for (const [key, tile] of this.tiles) {
      if (tile.dirty) continue;
      if (now - tile.lastAccess < MIN_TILE_AGE_MS) continue;
      if (this.renderingTiles.has(key)) continue;

      candidates.push({ key, lastAccess: tile.lastAccess });
    }

    if (candidates.length === 0) return;

    candidates.sort((a, b) => a.lastAccess - b.lastAccess);

    const overflow = this.tiles.size - MAX_CACHED_TILES;
    const evictCount = Math.min(candidates.length, Math.max(overflow, Math.ceil(candidates.length * 0.1)));

    for (let i = 0; i < evictCount; i++) {
      this.tiles.delete(candidates[i].key);
    }
  }

  // ===========================================
  // TILE ACCESS
  // ===========================================

  /**
   * Gets a tile with fallback to parent tiles (higher zoom = lower detail)
   * With inverted zoom: z=0 is highest detail, z=MAX_ZOOM is lowest detail
   * Parents are at z+1, children at z-1
   */
  private getTileWithFallback(z: number, x: number, y: number): CachedTile | null {
    // Try exact tile
    const key = getTileKey(z, x, y);
    const tile = this.tiles.get(key);
    if (tile && tile.hasContent) {
      return tile;
    }

    // Try parent tiles (higher zoom levels = lower detail) as fallback
    // With inverted system: parent is at z+1, so we go UP to MAX_ZOOM
    let currentX = x;
    let currentY = y;
    for (let fallbackZ = z + 1; fallbackZ <= MAX_ZOOM; fallbackZ++) {
      // Each step up halves the coordinates
      currentX = Math.floor(currentX / 2);
      currentY = Math.floor(currentY / 2);
      const parentKey = getTileKey(fallbackZ, currentX, currentY);

      const parentTile = this.tiles.get(parentKey);
      if (parentTile && parentTile.hasContent) {
        return parentTile;
      }
    }

    return tile || null;
  }

  // ===========================================
  // DRAWING (STROKE-BASED SYSTEM)
  // ===========================================

  /**
   * Draws a brush stroke at world coordinates
   * Stores the stroke and draws to overlay for preview
   */
  drawBrushStroke(
    worldX: number,
    worldY: number,
    materialName: string,
    brushName: string,
    size: number,
    rotation: number,
    strength: number
  ): void {
    const affectedTiles = this.getAffectedTiles(worldX, worldY, size);

    // Store the stroke
    const stroke: BrushStroke = {
      id: this.nextStrokeId++,
      worldX,
      worldY,
      materialName,
      brushName,
      size,
      rotation,
      strength,
      isEraser: false,
      affectedTiles
    };

    this.strokes.push(stroke);
    this.redoStack = []; // Clear redo stack on new stroke

    // Draw to overlay for preview
    this.applyStrokeToOverlay(stroke);
  }

  /**
   * Erases at world coordinates
   * Stores the stroke and draws to overlay for preview
   */
  eraseBrushStroke(
    worldX: number,
    worldY: number,
    brushName: string,
    size: number,
    rotation: number,
    strength: number
  ): void {
    const affectedTiles = this.getAffectedTiles(worldX, worldY, size);

    // Store the stroke
    const stroke: BrushStroke = {
      id: this.nextStrokeId++,
      worldX,
      worldY,
      materialName: null,
      brushName,
      size,
      rotation,
      strength,
      isEraser: true,
      affectedTiles
    };

    this.strokes.push(stroke);
    this.redoStack = [];

    this.applyStrokeToOverlay(stroke);
  }

  /**
   * Gets affected tile coordinates at DETAIL_ZOOM for a stroke
   */
  private getAffectedTiles(worldX: number, worldY: number, size: number): Array<{ x: number; y: number }> {
    const halfSize = size / 2;
    const minTile = worldToTile(worldX - halfSize, worldY - halfSize, DETAIL_ZOOM);
    const maxTile = worldToTile(worldX + halfSize, worldY + halfSize, DETAIL_ZOOM);

    const tiles: Array<{ x: number; y: number }> = [];
    for (let tx = minTile.x; tx <= maxTile.x; tx++) {
      for (let ty = minTile.y; ty <= maxTile.y; ty++) {
        tiles.push({ x: tx, y: ty });
      }
    }
    return tiles;
  }

  /**
   * Applies a single stroke to the overlay tiles for preview
   * Both brush and eraser now draw directly - eraser draws background texture
   */
  private applyStrokeToOverlay(stroke: BrushStroke): void {
    const stampCanvas = stroke.isEraser
      ? this.getOrCreateEraserStamp(stroke.brushName, stroke.size, stroke.strength)
      : this.getOrCreateStamp(stroke.materialName!, stroke.brushName, stroke.size, stroke.strength);

    if (!stampCanvas) return;

    const tileWorldSize = getTileWorldSize(DETAIL_ZOOM);

    for (const tileCoord of stroke.affectedTiles) {
      const key = getTileKey(DETAIL_ZOOM, tileCoord.x, tileCoord.y);

      // Get or create overlay tile
      let overlay = this.overlayTiles.get(key);
      if (!overlay) {
        overlay = document.createElement('canvas');
        overlay.width = TILE_SIZE;
        overlay.height = TILE_SIZE;
        this.overlayTiles.set(key, overlay);
      }

      const ctx = overlay.getContext('2d');
      if (!ctx) continue;

      const tileWorld = tileToWorld(tileCoord.x, tileCoord.y, DETAIL_ZOOM);
      const localX = (stroke.worldX - tileWorld.x) * (TILE_SIZE / tileWorldSize);
      const localY = (stroke.worldY - tileWorld.y) * (TILE_SIZE / tileWorldSize);

      // Both brush and eraser draw directly on overlay with source-over
      // Eraser stamp contains background texture, brush stamp contains material
      ctx.save();
      ctx.translate(localX, localY);
      ctx.rotate((stroke.rotation * Math.PI) / 180);
      ctx.globalCompositeOperation = 'source-over';
      ctx.drawImage(stampCanvas, -stampCanvas.width / 2, -stampCanvas.height / 2);
      ctx.restore();

      this.dirtyOverlayTiles.add(key);
    }
  }

  /**
   * Rebuilds all overlay tiles from stroke history
   * Used after undo/redo operations
   */
  private rebuildOverlays(): void {
    // Clear all overlays
    this.overlayTiles.clear();
    this.dirtyOverlayTiles.clear();

    // Reapply all strokes
    for (const stroke of this.strokes) {
      this.applyStrokeToOverlay(stroke);
    }
  }

  /**
   * Undo the last stroke
   */
  undo(): boolean {
    if (this.strokes.length === 0) return false;

    const stroke = this.strokes.pop()!;
    this.redoStack.push(stroke);

    this.rebuildOverlays();
    return true;
  }

  /**
   * Redo the last undone stroke
   */
  redo(): boolean {
    if (this.redoStack.length === 0) return false;

    const stroke = this.redoStack.pop()!;
    this.strokes.push(stroke);

    this.applyStrokeToOverlay(stroke);
    return true;
  }

  /**
   * Gets the overlay canvas for a tile (for rendering)
   * Returns overlay to draw on top of base tile
   * Optimized: only composites if there are actual overlays, uses direct iteration
   */
  getOverlay(z: number, x: number, y: number): HTMLCanvasElement | null {
    // Quick exit if no overlays exist at all
    if (this.overlayTiles.size === 0) return null;

    if (z === DETAIL_ZOOM) {
      return this.overlayTiles.get(getTileKey(DETAIL_ZOOM, x, y)) || null;
    }

    // For non-DETAIL_ZOOM (lower detail levels), check if any overlay falls within this tile's area
    // Higher z means lower detail, so we need to look at more detail tiles (lower z)
    // Instead of recursion, directly check which overlays are relevant
    const zoomDiff = z - DETAIL_ZOOM;
    const scale = Math.pow(2, zoomDiff);
    const minX = x * scale;
    const maxX = minX + scale - 1;
    const minY = y * scale;
    const maxY = minY + scale - 1;

    // Find relevant overlays by iterating once
    const relevantOverlays: Array<{ key: string; canvas: HTMLCanvasElement; x: number; y: number }> = [];
    for (const [key, canvas] of this.overlayTiles) {
      // Parse key to get coordinates (format: "z/x/y")
      const parts = key.split('/');
      const ox = parseInt(parts[1], 10);
      const oy = parseInt(parts[2], 10);

      if (ox >= minX && ox <= maxX && oy >= minY && oy <= maxY) {
        relevantOverlays.push({ key, canvas, x: ox, y: oy });
      }
    }

    if (relevantOverlays.length === 0) return null;

    // Create composite canvas
    const compositeCanvas = document.createElement('canvas');
    compositeCanvas.width = TILE_SIZE;
    compositeCanvas.height = TILE_SIZE;
    const ctx = compositeCanvas.getContext('2d');
    if (!ctx) return null;

    // Draw each relevant overlay at its correct position
    const subTileSize = TILE_SIZE / scale;
    for (const overlay of relevantOverlays) {
      const localX = overlay.x - minX;
      const localY = overlay.y - minY;
      const destX = localX * subTileSize;
      const destY = localY * subTileSize;
      ctx.drawImage(overlay.canvas, 0, 0, TILE_SIZE, TILE_SIZE, destX, destY, subTileSize, subTileSize);
    }

    return compositeCanvas;
  }

  // ===========================================
  // RENDERING
  // ===========================================

  /**
   * Renders tiles to canvas with overlay compositing
   * Optimized to reduce per-frame allocations and repeated calculations
   */
  renderToCanvas(
    ctx: CanvasRenderingContext2D,
    viewportX: number,
    viewportY: number,
    centerX: number,
    centerY: number,
    viewportZoom: number
  ): void {
    const canvasWidth = ctx.canvas.width / (window.devicePixelRatio || 1);
    const canvasHeight = ctx.canvas.height / (window.devicePixelRatio || 1);
    const optimalZoom = getOptimalZoom(viewportZoom);

    this.renderingTiles.clear();

    // Use cached visible tiles if viewport hasn't changed significantly
    let visibleTiles: TileCoord[];
    const cache = this.visibleTilesCache;
    if (cache &&
        Math.abs(cache.viewportX - viewportX) < 1 &&
        Math.abs(cache.viewportY - viewportY) < 1 &&
        cache.viewportZoom === viewportZoom &&
        cache.canvasWidth === canvasWidth &&
        cache.canvasHeight === canvasHeight) {
      visibleTiles = cache.tiles;
    } else {
      visibleTiles = getVisibleTiles(
        viewportX, viewportY, canvasWidth, canvasHeight, viewportZoom, optimalZoom
      );
      this.visibleTilesCache = {
        tiles: visibleTiles,
        viewportX,
        viewportY,
        viewportZoom,
        canvasWidth,
        canvasHeight
      };
    }

    // Pre-calculate values used for all tiles at this zoom level
    const tileWorldSize = getTileWorldSize(optimalZoom);
    const screenSize = tileWorldSize * viewportZoom;
    const now = Date.now();
    const hasOverlays = this.overlayTiles.size > 0;

    // Fade duration in milliseconds
    const FADE_DURATION = 200;

    for (const tileCoord of visibleTiles) {
      const key = getTileKey(tileCoord.z, tileCoord.x, tileCoord.y);
      this.renderingTiles.add(key);

      const tile = this.getTileWithFallback(tileCoord.z, tileCoord.x, tileCoord.y);

      const tileWorld = tileToWorld(tileCoord.x, tileCoord.y, tileCoord.z);

      const screenX = (tileWorld.x - viewportX) * viewportZoom + centerX;
      const screenY = (tileWorld.y - viewportY) * viewportZoom + centerY;

      // Draw base tile if available
      if (tile && tile.hasContent) {
        // Update opacity for fade-in animation
        if (tile.opacity < 1) {
          const elapsed = now - tile.appearTime;
          tile.opacity = Math.min(1, elapsed / FADE_DURATION);
        }

        // Apply opacity if not fully opaque
        const needsOpacity = tile.opacity < 1;
        if (needsOpacity) {
          ctx.globalAlpha = tile.opacity;
        }

        // Check if using fallback (tile from parent zoom level)
        // With inverted system: parent tiles are at higher z (lower detail)
        if (tile.z !== tileCoord.z) {
          // Extract correct portion from parent tile
          // tile.z > tileCoord.z means parent is at higher zoom (lower detail)
          const zoomDiff = tile.z - tileCoord.z; // Now positive when using parent
          const scale = Math.pow(2, zoomDiff);
          const subTileSize = TILE_SIZE / scale;

          // Calculate which portion of the parent tile to extract
          // tileCoord represents a sub-tile within the parent
          const localX = tileCoord.x % scale;
          const localY = tileCoord.y % scale;

          const srcX = localX * subTileSize;
          const srcY = localY * subTileSize;

          ctx.drawImage(
            tile.canvas,
            srcX, srcY, subTileSize, subTileSize,
            screenX, screenY, screenSize, screenSize
          );
        } else {
          ctx.drawImage(tile.canvas, screenX, screenY, screenSize, screenSize);
        }

        // Restore opacity
        if (needsOpacity) {
          ctx.globalAlpha = 1;
        }

        tile.lastAccess = now;
      }

      // Draw overlay on top (for brush/eraser stroke preview)
      // Skip overlay check entirely if no overlays exist
      if (hasOverlays) {
        const overlay = this.getOverlay(tileCoord.z, tileCoord.x, tileCoord.y);
        if (overlay) {
          ctx.drawImage(overlay, screenX, screenY, screenSize, screenSize);
        }
      }
    }

    this.renderingTiles.clear();

  }

  // ===========================================
  // SAVING
  // ===========================================

  // Chunk size for batch saves (prevents payload too large and reduces freeze)
  private static readonly SAVE_CHUNK_SIZE = 50;

  /**
   * Yields control back to the browser to prevent UI freeze
   */
  private async yieldToMain(): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, 0));
  }

  /**
   * Loads a tile from server directly (bypassing cache) for save operation
   */
  private async loadTileFromServer(z: number, x: number, y: number): Promise<HTMLCanvasElement> {
    const baseUrl = tilesApi.getTileUrl(this.mapId, z, x, y);
    const url = `${baseUrl}?_t=${Date.now()}`;

    return new Promise((resolve) => {
      const img = new Image();

      img.onload = () => {
        createImageBitmap(img).then(bitmap => {
          const canvas = document.createElement('canvas');
          canvas.width = TILE_SIZE;
          canvas.height = TILE_SIZE;
          const ctx = canvas.getContext('2d');

          if (ctx) {
            ctx.drawImage(bitmap, 0, 0);
          }
          bitmap.close();
          resolve(canvas);
        }).catch(() => {
          // Return empty canvas on error
          const canvas = document.createElement('canvas');
          canvas.width = TILE_SIZE;
          canvas.height = TILE_SIZE;
          resolve(canvas);
        });
      };

      img.onerror = () => {
        // Tile doesn't exist on server - return empty canvas
        const canvas = document.createElement('canvas');
        canvas.width = TILE_SIZE;
        canvas.height = TILE_SIZE;
        resolve(canvas);
      };

      img.src = url;
    });
  }

  /**
   * Applies a stroke to a canvas at DETAIL_ZOOM level for SAVING
   * Eraser uses destination-out to actually erase pixels (not draw background)
   */
  private applyStrokeToCanvas(ctx: CanvasRenderingContext2D, stroke: BrushStroke, tileX: number, tileY: number): void {
    // For saving, eraser uses the real eraser stamp (black mask) with destination-out
    const stampCanvas = stroke.isEraser
      ? this.getOrCreateRealEraserStamp(stroke.brushName, stroke.size, stroke.strength)
      : this.getOrCreateStamp(stroke.materialName!, stroke.brushName, stroke.size, stroke.strength);

    if (!stampCanvas) return;

    const tileWorldSize = getTileWorldSize(DETAIL_ZOOM);
    const compositeOp: GlobalCompositeOperation = stroke.isEraser ? 'destination-out' : 'source-over';

    const tileWorld = tileToWorld(tileX, tileY, DETAIL_ZOOM);
    const localX = (stroke.worldX - tileWorld.x) * (TILE_SIZE / tileWorldSize);
    const localY = (stroke.worldY - tileWorld.y) * (TILE_SIZE / tileWorldSize);

    ctx.save();
    ctx.translate(localX, localY);
    ctx.rotate((stroke.rotation * Math.PI) / 180);
    ctx.globalCompositeOperation = compositeOp;
    ctx.drawImage(stampCanvas, -stampCanvas.width / 2, -stampCanvas.height / 2);
    ctx.restore();
  }

  /**
   * Saves all strokes by loading real z0 tiles from server, applying strokes, then saving
   * This preserves full quality since we always use the actual detail tiles as base
   *
   * IMPORTANT: This method captures strokes at the start and only removes those specific
   * strokes after saving. Any new strokes added during the save operation are preserved.
   */
  async saveAllDirtyTiles(): Promise<void> {
    if (this.strokes.length === 0) return;

    // Capture the strokes to save at this moment - any new strokes added during
    // the save process will be preserved for the next save
    const strokesToSave = [...this.strokes];
    const strokeCountAtStart = strokesToSave.length;

    // Collect all unique affected tiles at DETAIL_ZOOM
    const affectedTilesMap = new Map<string, { x: number; y: number }>();
    for (const stroke of strokesToSave) {
      for (const tile of stroke.affectedTiles) {
        const key = `${tile.x},${tile.y}`;
        affectedTilesMap.set(key, tile);
      }
    }

    const affectedTiles = Array.from(affectedTilesMap.values());
    if (affectedTiles.length === 0) return;

    // Prepare tiles to save: load from server, apply strokes, encode
    const tilesToSave: Array<{ z: number; x: number; y: number; data: string }> = [];
    const detailZoomTiles: Array<{ x: number; y: number }> = [];

    // Process tiles in batches to avoid loading too many at once
    const batchSize = 20;
    for (let i = 0; i < affectedTiles.length; i += batchSize) {
      const batch = affectedTiles.slice(i, i + batchSize);

      // Load all tiles in this batch from server
      const loadPromises = batch.map(async (tile) => {
        const canvas = await this.loadTileFromServer(DETAIL_ZOOM, tile.x, tile.y);
        return { tile, canvas };
      });

      const loadedTiles = await Promise.all(loadPromises);
      await this.yieldToMain();

      // Apply strokes to each loaded tile (only the strokes we captured at the start)
      for (const { tile, canvas } of loadedTiles) {
        const ctx = canvas.getContext('2d');
        if (!ctx) continue;

        // Apply all strokes that affect this tile
        for (const stroke of strokesToSave) {
          const affectsThisTile = stroke.affectedTiles.some(
            t => t.x === tile.x && t.y === tile.y
          );
          if (affectsThisTile) {
            this.applyStrokeToCanvas(ctx, stroke, tile.x, tile.y);
          }
        }

        // Encode and add to save list
        const dataUrl = canvas.toDataURL('image/png');
        const base64 = dataUrl.split(',')[1];

        tilesToSave.push({
          z: DETAIL_ZOOM,
          x: tile.x,
          y: tile.y,
          data: base64
        });

        detailZoomTiles.push({ x: tile.x, y: tile.y });

        // Also update our local cache with the composited result
        // But first, apply any new strokes that were added during this save operation
        // to keep the visual consistent with what the user has drawn
        const newStrokes = this.strokes.slice(strokeCountAtStart);
        for (const stroke of newStrokes) {
          const affectsThisTile = stroke.affectedTiles.some(
            t => t.x === tile.x && t.y === tile.y
          );
          if (affectsThisTile) {
            this.applyStrokeToCanvas(ctx, stroke, tile.x, tile.y);
          }
        }

        const key = getTileKey(DETAIL_ZOOM, tile.x, tile.y);
        const now = Date.now();
        this.tiles.set(key, {
          canvas,
          dirty: false,
          lastAccess: now,
          hasContent: true,
          z: DETAIL_ZOOM,
          x: tile.x,
          y: tile.y,
          opacity: 1,
          appearTime: now
        });
      }

      await this.yieldToMain();
    }

    if (tilesToSave.length === 0) return;

    // Save in chunks
    const chunkSize = TilePyramidManager.SAVE_CHUNK_SIZE;
    const chunks: Array<Array<{ z: number; x: number; y: number; data: string }>> = [];

    for (let i = 0; i < tilesToSave.length; i += chunkSize) {
      chunks.push(tilesToSave.slice(i, i + chunkSize));
    }

    try {
      // Save chunks sequentially to avoid overwhelming the server
      for (const chunk of chunks) {
        await tilesApi.saveTilesBatch(this.mapId, chunk);
        await this.yieldToMain();
      }

      // Ask server to regenerate the pyramid for affected tiles
      if (detailZoomTiles.length > 0) {
        await tilesApi.regeneratePyramid(this.mapId, DETAIL_ZOOM, detailZoomTiles);
      }

      // Invalidate all parent tiles in cache so they get reloaded with new data
      // This ensures the user sees the updated tiles at all zoom levels
      this.invalidateAncestorTiles(detailZoomTiles);

      // Only remove the strokes we actually saved - preserve any new strokes added during save
      // This prevents losing user edits made while the save was in progress
      this.strokes = this.strokes.slice(strokeCountAtStart);

      // Only clear redo stack if we saved all strokes (no new ones added)
      if (this.strokes.length === 0) {
        this.redoStack = [];
      }

      // Clear dirty tiles tracking
      this.dirtyTiles.clear();

      // Rebuild overlays for any remaining strokes (added during save)
      // This ensures the preview is visible for strokes not yet saved
      this.rebuildOverlays();
    } catch (err) {
      console.error('Failed to save tiles:', err);
      throw err;
    }
  }

  /**
   * Invalidates (removes from cache) ancestor/parent tiles affected by the save.
   * Does NOT invalidate DETAIL_ZOOM tiles since they were already updated in the cache
   * with the composited result (including any new strokes added during save).
   * Only invalidates parent tiles (higher zoom levels = lower detail) so they reload
   * from the server with the newly saved content.
   */
  private invalidateAncestorTiles(tiles: Array<{ x: number; y: number }>): void {
    const keysToDelete: string[] = [];
    const detailKeysToMarkForCacheBust: string[] = [];

    for (const tile of tiles) {
      // Mark detail zoom tile for cache-busting but DON'T delete from local cache
      // since we already updated it with the composited result
      detailKeysToMarkForCacheBust.push(getTileKey(DETAIL_ZOOM, tile.x, tile.y));

      // Walk up the pyramid from DETAIL_ZOOM+1 to MAX_ZOOM (lower detail)
      // These parent tiles need to be invalidated so they reload from server
      let currentX = tile.x;
      let currentY = tile.y;
      for (let z = DETAIL_ZOOM + 1; z <= MAX_ZOOM; z++) {
        currentX = Math.floor(currentX / 2);
        currentY = Math.floor(currentY / 2);

        const key = getTileKey(z, currentX, currentY);
        keysToDelete.push(key);
      }
    }

    // Mark detail tiles for cache-busting (for future reloads) but don't delete
    for (const key of detailKeysToMarkForCacheBust) {
      this.tilesToBustCache.add(key);
    }

    // Remove duplicates and delete ONLY parent tiles from cache
    const uniqueKeys = [...new Set(keysToDelete)];
    for (const key of uniqueKeys) {
      this.tiles.delete(key);
      // Mark for cache-busting on reload
      this.tilesToBustCache.add(key);
    }

    // Increment cache version to bust browser HTTP cache
    this.cacheVersion++;

    // Clear the load request cache to force a fresh load
    this.lastLoadRequest = null;

    // Trigger reload of visible tiles using last known viewport
    if (this.lastViewport) {
      this.ensureTilesLoaded(
        this.lastViewport.x,
        this.lastViewport.y,
        this.lastViewport.width,
        this.lastViewport.height,
        this.lastViewport.zoom
      );
    }
  }

  /**
   * Checks if there are unsaved changes (any strokes pending)
   */
  hasUnsavedChanges(): boolean {
    return this.strokes.length > 0;
  }

  /**
   * Returns the number of pending strokes
   */
  getPendingStrokesCount(): number {
    return this.strokes.length;
  }

  /**
   * Returns whether undo is available
   */
  canUndo(): boolean {
    return this.strokes.length > 0;
  }

  /**
   * Returns whether redo is available
   */
  canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  // ===========================================
  // UTILITIES
  // ===========================================

  /**
   * Clears all cached data including strokes and overlays
   */
  clear(): void {
    this.tiles.clear();
    this.dirtyTiles.clear();
    this.loadingTiles.clear();
    this.loadQueue = [];
    this.activeLoads = 0;
    this.stampCache.clear();
    this.eraserStampCache.clear();
    this.renderingTiles.clear();
    // Clear stroke-based system
    this.strokes = [];
    this.redoStack = [];
    this.overlayTiles.clear();
    this.dirtyOverlayTiles.clear();
    // Clear cache-busting set
    this.tilesToBustCache.clear();
  }

  /**
   * Returns debug info
   */
  getDebugInfo(): {
    totalTiles: number;
    dirtyTiles: number;
    loadingTiles: number;
    cacheSize: number;
    pendingStrokes: number;
    overlayTiles: number;
    redoStackSize: number;
  } {
    return {
      totalTiles: this.tiles.size,
      dirtyTiles: this.dirtyTiles.size,
      loadingTiles: this.loadingTiles.size,
      cacheSize: this.tiles.size,
      pendingStrokes: this.strokes.length,
      overlayTiles: this.overlayTiles.size,
      redoStackSize: this.redoStack.length
    };
  }

  /**
   * Ensures tiles are loaded for brush drawing area
   */
  async ensureBrushTilesLoaded(worldX: number, worldY: number, size: number): Promise<void> {
    const halfSize = size / 2;
    const minTile = worldToTile(worldX - halfSize, worldY - halfSize, DETAIL_ZOOM);
    const maxTile = worldToTile(worldX + halfSize, worldY + halfSize, DETAIL_ZOOM);

    const tilesToLoad: TileCoord[] = [];

    for (let x = minTile.x; x <= maxTile.x; x++) {
      for (let y = minTile.y; y <= maxTile.y; y++) {
        const key = getTileKey(DETAIL_ZOOM, x, y);
        if (!this.tiles.has(key) && !this.loadingTiles.has(key)) {
          tilesToLoad.push({ z: DETAIL_ZOOM, x, y });
        }
      }
    }

    if (tilesToLoad.length === 0) return;

    // Load all needed tiles
    await Promise.all(tilesToLoad.map(t => this.loadTile(t.z, t.x, t.y)));
  }
}
